import { createTheme } from '@mui/material/styles';

// Paleta de cores da marca GT (Gestão Tributária)
const brandColors = {
  primary: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#1341A1', // Cor principal da marca
    600: '#1e88e5',
    700: '#1976d2',
    800: '#142F68', // Azul mais escuro
    900: '#0d47a1',
  },
  secondary: {
    50: '#fff3e0',
    100: '#ffe0b2',
    200: '#ffcc80',
    300: '#ffb74d',
    400: '#ffa726',
    500: '#ff6829', // Laranja da marca
    600: '#fb8c00',
    700: '#f57c00',
    800: '#ef6c00',
    900: '#e65100',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  success: {
    50: '#e8f5e8',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50',
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
  },
  warning: {
    50: '#fff8e1',
    100: '#ffecb3',
    200: '#ffe082',
    300: '#ffd54f',
    400: '#ffca28',
    500: '#F3B80C',
    600: '#ffa000',
    700: '#ff8f00',
    800: '#ff6f00',
    900: '#e65100',
  },
  error: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#d32f2f',
    600: '#c62828',
    700: '#b71c1c',
    800: '#a00000',
    900: '#8b0000',
  },
};

// Tokens de design
const designTokens = {
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },
  borderRadius: {
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
    full: 9999,
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    card: '0 4px 20px rgba(19, 65, 161, 0.08)',
    button: '0 2px 8px rgba(19, 65, 161, 0.15)',
  },
  typography: {
    fontFamily: '"Inter", "Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeights: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
};

// Tema Material-UI customizado
const modernTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandColors.primary[500],
      light: brandColors.primary[300],
      dark: brandColors.primary[800],
      contrastText: '#ffffff',
    },
    secondary: {
      main: brandColors.secondary[500],
      light: brandColors.secondary[300],
      dark: brandColors.secondary[700],
      contrastText: '#ffffff',
    },
    error: {
      main: brandColors.error[500],
      light: brandColors.error[300],
      dark: brandColors.error[700],
    },
    warning: {
      main: brandColors.warning[500],
      light: brandColors.warning[300],
      dark: brandColors.warning[700],
    },
    success: {
      main: brandColors.success[500],
      light: brandColors.success[300],
      dark: brandColors.success[700],
    },
    grey: brandColors.neutral,
    background: {
      default: '#fafafa',
      paper: '#ffffff',
      alt: brandColors.neutral[50],
    },
    text: {
      primary: brandColors.neutral[900],
      secondary: brandColors.neutral[600],
      disabled: brandColors.neutral[400],
    },
    divider: brandColors.neutral[200],
  },
  typography: {
    fontFamily: designTokens.typography.fontFamily,
    h1: {
      fontSize: '2.5rem',
      fontWeight: designTokens.typography.fontWeights.bold,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: designTokens.typography.fontWeights.bold,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: designTokens.typography.fontWeights.semibold,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: designTokens.typography.fontWeights.semibold,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: designTokens.typography.fontWeights.medium,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: designTokens.typography.fontWeights.medium,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: designTokens.typography.fontWeights.regular,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: designTokens.typography.fontWeights.regular,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: designTokens.typography.fontWeights.medium,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: designTokens.typography.fontWeights.regular,
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: designTokens.borderRadius.md,
  },
  spacing: designTokens.spacing.sm,
  shadows: [
    'none',
    designTokens.shadows.sm,
    designTokens.shadows.md,
    designTokens.shadows.lg,
    designTokens.shadows.xl,
    designTokens.shadows.card,
    designTokens.shadows.button,
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.md,
          textTransform: 'none',
          fontWeight: designTokens.typography.fontWeights.medium,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: designTokens.shadows.button,
          },
        },
        contained: {
          '&:hover': {
            boxShadow: designTokens.shadows.button,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
          boxShadow: designTokens.shadows.card,
          border: `1px solid ${brandColors.neutral[100]}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: designTokens.borderRadius.md,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: brandColors.primary[300],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brandColors.primary[500],
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
        },
        elevation1: {
          boxShadow: designTokens.shadows.sm,
        },
        elevation2: {
          boxShadow: designTokens.shadows.md,
        },
        elevation3: {
          boxShadow: designTokens.shadows.lg,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: designTokens.shadows.md,
          backgroundColor: '#ffffff',
          color: brandColors.neutral[900],
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${brandColors.neutral[200]}`,
          boxShadow: 'none',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.neutral[300],
        },
      },
    },
  },
});

export { brandColors, designTokens, modernTheme };
export default modernTheme;